import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box,
  Typography, Grid, Chip, Divider, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, IconButton, Stack,
  LinearProgress, useTheme, alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  PictureAsPdf as PdfIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend,
} from 'recharts';
import { Report } from './Report';
import { ReportPDFGenerator } from './reportPDFGenerator';
import { useCompany } from '../../../contexts/CompanyContext';
import { getCurrencySymbol } from '../../../util/productUtils';
import { CHART_PALETTE, STATUS_CHART_COLORS, colorForSeriesIndex } from './reportChartTheme';

interface Props {
  open: boolean;
  report: Report;
  onClose: () => void;
}

// ── Theme-aware chip style helper ─────────────────────────────────────────────
//
// In light mode: soft pastel tints (low-opacity fills).
// In dark mode:  richer semi-transparent fills + brighter text so the chip
//                is legible against dark paper backgrounds.
//
// Pass `isDark` (from useTheme) to switch between the two palettes.
// ─────────────────────────────────────────────────────────────────────────────
const getSoftChipSx = (color: string, isDark: boolean = false) => {
  type PaletteEntry = { bg: string; border: string; text: string; iconColor: string };

  const lightPalette: Record<string, PaletteEntry> = {
    warning:   { bg: 'rgba(255,167,38,0.12)',  border: 'rgba(255,167,38,0.4)',   text: '#b76e00', iconColor: '#e68900' },
    info:      { bg: 'rgba(41,182,246,0.12)',   border: 'rgba(41,182,246,0.4)',   text: '#0277bd', iconColor: '#039be5' },
    primary:   { bg: 'rgba(33,150,243,0.12)',   border: 'rgba(33,150,243,0.4)',   text: '#1565c0', iconColor: '#1976d2' },
    success:   { bg: 'rgba(102,187,106,0.15)',  border: 'rgba(102,187,106,0.45)', text: '#2e7d32', iconColor: '#43a047' },
    error:     { bg: 'rgba(239,83,80,0.12)',    border: 'rgba(239,83,80,0.4)',    text: '#c62828', iconColor: '#e53935' },
    secondary: { bg: 'rgba(171,71,188,0.10)',   border: 'rgba(171,71,188,0.35)',  text: '#6a1b9a', iconColor: '#8e24aa' },
    default:   { bg: 'rgba(158,158,158,0.12)',  border: 'rgba(158,158,158,0.4)',  text: '#424242', iconColor: '#757575' },
  };

  // Dark mode: higher-opacity fills + light/vivid text so chips pop on dark surfaces
  const darkPalette: Record<string, PaletteEntry> = {
    warning:   { bg: 'rgba(255,167,38,0.22)',  border: 'rgba(255,167,38,0.55)',  text: '#ffcc80', iconColor: '#ffa726' },
    info:      { bg: 'rgba(41,182,246,0.20)',   border: 'rgba(41,182,246,0.50)',  text: '#81d4fa', iconColor: '#29b6f6' },
    primary:   { bg: 'rgba(33,150,243,0.22)',   border: 'rgba(33,150,243,0.50)',  text: '#90caf9', iconColor: '#42a5f5' },
    success:   { bg: 'rgba(102,187,106,0.22)',  border: 'rgba(102,187,106,0.50)', text: '#a5d6a7', iconColor: '#66bb6a' },
    error:     { bg: 'rgba(239,83,80,0.22)',    border: 'rgba(239,83,80,0.50)',   text: '#ef9a9a', iconColor: '#ef5350' },
    secondary: { bg: 'rgba(171,71,188,0.22)',   border: 'rgba(171,71,188,0.50)',  text: '#ce93d8', iconColor: '#ab47bc' },
    default:   { bg: 'rgba(158,158,158,0.22)',  border: 'rgba(158,158,158,0.50)', text: '#e0e0e0', iconColor: '#bdbdbd' },
  };

  const palette = isDark ? darkPalette : lightPalette;
  const p = palette[color] ?? palette.default;

  return {
    backgroundColor: p.bg,
    borderColor: p.border,
    color: p.text,
    fontWeight: 600,
    border: `1px solid ${p.border}`,
    '& .MuiChip-icon': { color: p.iconColor },
    '& .MuiChip-label': { color: p.text },
  };
};

// ── Map an order status string → chip colour key ─────────────────────────────
const getOrderStatusColor = (status: string): string => {
  const s = status.toLowerCase();
  if (s === 'delivered')                                             return 'success';
  if (s === 'shipped' || s === 'confirmed')                         return 'info';
  if (s === 'processing')                                           return 'primary';
  if (s === 'pending')                                              return 'warning';
  if (s === 'cancelled' || s === 'returned' || s === 'refunded')   return 'error';
  return 'default';
};

// ── Number formatting helpers ────────────────────────────────────────────────
const fmt = (n: number, sym: string) =>
  `${sym} ${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtNum = (n: number) =>
  n.toLocaleString('en-US');

// Truncates long category/product/supplier names before they hit a rotated
// chart axis — without this, long names at a shallow rotation angle overlap
// each other and become unreadable.
const truncateLabel = (value: string, max: number = 14): string =>
  value.length > max ? `${value.slice(0, max - 1)}…` : value;

// Shared rotated-category-axis props for bar charts with long text labels
// (product/supplier/category names). A steeper angle than a shallow -15°
// keeps consecutive labels from overlapping, and the taller reserved height
// plus truncated text keeps them legible instead of clipped.
const rotatedAxisProps = {
  tickFormatter: truncateLabel,
  interval: 0 as const,
  angle: -35,
  textAnchor: 'end' as const,
  height: 64,
};

// ── Summary card ─────────────────────────────────────────────────────────────
// Uses `cardBg` (background.default) so cards appear visually distinct
// from the dialog surface (background.paper) — matching the supplier drawer
// pattern where drawerBg = background.default and cardBg = background.paper.
// In the report dialog the surface is background.paper, so cards use
// background.default to achieve the same contrast relationship.
// ─────────────────────────────────────────────────────────────────────────────
interface SummaryCardProps {
  label: string;
  value: React.ReactNode;
  cardBg: string;
  // No longer rendered — every call site used to pass a distinct palette
  // color for a per-card top border, which made the stat card row look like
  // a rainbow. Kept optional so existing call sites don't need to change.
  accentColor?: string;
}

const SummaryCard: React.FC<SummaryCardProps> = ({ label, value, cardBg }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2,
      bgcolor: cardBg,
      backgroundImage: 'none',
      borderRadius: 2,
      height: '100%',
      position: 'relative',
      overflow: 'hidden',
      transition: 'box-shadow 0.15s ease, transform 0.15s ease',
      '&:hover': { boxShadow: 3 },
    }}
  >
    <Typography variant="caption" color="text.secondary" display="block" gutterBottom sx={{ fontWeight: 600, letterSpacing: 0.2 }}>
      {label}
    </Typography>
    <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 700 }}>
      {value}
    </Typography>
  </Paper>
);

// ── Chart card wrapper ───────────────────────────────────────────────────────
// Wraps a single recharts chart in a themed Paper "tile" matching SummaryCard,
// with a title header and a fixed-height responsive plotting area.
// ─────────────────────────────────────────────────────────────────────────────
interface ChartCardProps {
  title: string;
  subtitle?: string;
  cardBg: string;
  height?: number;
  children: React.ReactElement;
}

const ChartCard: React.FC<ChartCardProps> = ({ title, subtitle, cardBg, height = 280, children }) => (
  <Paper
    variant="outlined"
    sx={{ p: 2, bgcolor: cardBg, backgroundImage: 'none', borderRadius: 2, height: '100%' }}
  >
    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        {subtitle}
      </Typography>
    )}
    <Box sx={{ width: '100%', height, mt: subtitle ? 0.5 : 1.5 }}>
      <ResponsiveContainer width="100%" height="100%">
        {children}
      </ResponsiveContainer>
    </Box>
  </Paper>
);

// ─────────────────────────────────────────────────────────────────────────────

const ReportDetailsDialog: React.FC<Props> = ({ open, report, onClose }) => {
  const { companyProfile } = useCompany();
  const companyCurrency = companyProfile?.currency || 'LKR';
  const sym             = getCurrencySymbol(companyCurrency);

  const theme  = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // ── Colors sourced entirely from the MUI theme (set via themeConfig.ts) ──
  // dialogBg → background.paper   (the dialog surface)
  // cardBg   → background.default (cards sit on top — darker bg gives contrast)
  //
  // This mirrors the supplier drawer pattern:
  //   drawerBg = background.default  (shell/outer)
  //   cardBg   = background.paper    (cards inside)
  // Here the dialog IS background.paper, so we invert:
  //   dialogBg = background.paper
  //   cardBg   = background.default  (slightly darker → visible card contrast)
  // ─────────────────────────────────────────────────────────────────────────
  const dialogBg = theme.palette.background.default;
  const cardBg   = theme.palette.background.paper;

  // ── Shared recharts theming (dark-mode aware) ─────────────────────────────
  const gridStroke   = theme.palette.divider;
  const axisTickFill = theme.palette.text.secondary;
  const axisStyle    = { fontSize: 11, fill: axisTickFill };
  const tooltipContentStyle = {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    fontSize: 12,
    boxShadow: theme.shadows[3],
  };
  const tooltipLabelStyle = { color: theme.palette.text.primary, fontWeight: 600 };
  const legendTextStyle   = { fontSize: 12, color: theme.palette.text.secondary };
  // Recharts' Tooltip defaults its hover-highlight `cursor` to a fixed grey
  // fill/stroke, unrelated to the theme — left as-is it's a washed-out
  // light-grey box on dark-mode charts. Themed to a faint tint of the
  // foreground color so it reads correctly in both modes.
  const tooltipCursor = { fill: alpha(theme.palette.text.primary, isDark ? 0.08 : 0.04) };

  const handleDownloadPDF = async () => {
    try { await ReportPDFGenerator.generatePDF(report, companyCurrency); }
    catch { alert('Failed to download PDF'); }
  };

  // ── Sales ──────────────────────────────────────────────────────────────────
  const renderSalesReport = () => {
    const data = report.data;
    if (!data.orders) return null;
    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Summary</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.slate} label="Total Sales"     value={fmtNum(data.summary.totalOrders)} /></Grid>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.green} label="Total Revenue"   value={fmt(data.summary.totalRevenue, sym)} /></Grid>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.teal}  label="Avg Sale Price"  value={fmt(data.summary.averageOrderValue, sym)} /></Grid>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.amber} label="Trade-In Credit" value={fmt(data.summary.totalDiscount, sym)} /></Grid>
        </Grid>

        {(data.summary.topProducts?.length > 0 || (data.summary.statusBreakdown && Object.keys(data.summary.statusBreakdown).length > 0)) && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {data.summary.topProducts?.length > 0 && (
              <Grid item xs={12} md={7}>
                <ChartCard title="Best-Selling Vehicles" subtitle="Top 5 by revenue" cardBg={cardBg}>
                  <BarChart data={data.summary.topProducts.slice(0, 5).map((p: any) => ({ name: p.productName, Revenue: p.revenue }))} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                    <XAxis dataKey="name" tick={axisStyle} {...rotatedAxisProps} />
                    <YAxis tick={axisStyle} width={70} tickFormatter={(v) => fmtNum(v)} />
                    <RechartsTooltip contentStyle={tooltipContentStyle} labelStyle={tooltipLabelStyle} cursor={tooltipCursor} formatter={(v: number) => fmt(v, sym)} />
                    <Bar dataKey="Revenue" radius={[4, 4, 0, 0]} maxBarSize={48}>
                      {data.summary.topProducts.slice(0, 5).map((_: any, i: number) => (
                        <Cell key={i} fill={colorForSeriesIndex(i)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ChartCard>
              </Grid>
            )}
            {data.summary.statusBreakdown && Object.keys(data.summary.statusBreakdown).length > 0 && (
              <Grid item xs={12} md={5}>
                <ChartCard title="Status Breakdown" cardBg={cardBg}>
                  <PieChart>
                    <Pie
                      data={Object.entries(data.summary.statusBreakdown).map(([name, value]) => ({ name, value }))}
                      dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={2}
                    >
                      {Object.keys(data.summary.statusBreakdown).map((status: string, i: number) => (
                        <Cell key={status} fill={STATUS_CHART_COLORS[getOrderStatusColor(status)] || colorForSeriesIndex(i)} />
                      ))}
                    </Pie>
                    <RechartsTooltip contentStyle={tooltipContentStyle} labelStyle={tooltipLabelStyle} cursor={tooltipCursor} />
                    <Legend wrapperStyle={legendTextStyle} />
                  </PieChart>
                </ChartCard>
              </Grid>
            )}
          </Grid>
        )}

        <Typography variant="h6" gutterBottom>Sold Vehicles</Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, bgcolor: cardBg, backgroundImage: 'none', borderRadius: 1.5 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>VIN</TableCell><TableCell>Buyer</TableCell><TableCell>Sale Date</TableCell>
                <TableCell align="right">Price</TableCell><TableCell>Payment</TableCell><TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.orders.slice(0, 50).map((o: any) => (
                <TableRow key={o.orderId}>
                  <TableCell>{o.orderNumber}</TableCell>
                  <TableCell>{o.customerName}</TableCell>
                  <TableCell>{o.orderDate instanceof Date ? o.orderDate.toLocaleDateString() : 'N/A'}</TableCell>
                  <TableCell align="right">{fmt(o.totalAmount, sym)}</TableCell>
                  <TableCell><Chip label={o.paymentMethod} size="small" variant="outlined" sx={getSoftChipSx('primary', isDark)} /></TableCell>
                  <TableCell><Chip label={o.status}        size="small" variant="outlined" sx={getSoftChipSx(getOrderStatusColor(o.status), isDark)} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // ── Inventory ──────────────────────────────────────────────────────────────
  const renderInventoryReport = () => {
    const data = report.data;
    if (!data.products) return null;
    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Summary</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4}><SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.slate} label="Total Products" value={fmtNum(data.summary.totalProducts)} /></Grid>
          <Grid item xs={6} sm={4}><SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.teal}  label="Total Stock"    value={fmtNum(data.summary.totalStock)} /></Grid>
          <Grid item xs={6} sm={4}><SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.green} label="Stock Value"    value={fmt(data.summary.totalValue, sym)} /></Grid>
        </Grid>

        {(() => {
          const statusCounts: Record<string, number> = {};
          const categoryValue: Record<string, number> = {};
          data.products.forEach((p: any) => {
            statusCounts[p.status] = (statusCounts[p.status] || 0) + 1;
            categoryValue[p.category] = (categoryValue[p.category] || 0) + (p.stockValue || 0);
          });
          const categoryData = Object.entries(categoryValue)
            .sort((a, b) => (b[1] as number) - (a[1] as number))
            .slice(0, 6)
            .map(([name, value]) => ({ name, Value: value }));
          return (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={5}>
                <ChartCard title="Stock Status" cardBg={cardBg}>
                  <PieChart>
                    <Pie
                      data={Object.entries(statusCounts).map(([name, value]) => ({ name: name.replace('_', ' '), value }))}
                      dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={2}
                    >
                      {Object.keys(statusCounts).map((status: string, i: number) => (
                        <Cell key={status} fill={STATUS_CHART_COLORS[status === 'in_stock' ? 'success' : status === 'low_stock' ? 'warning' : 'error'] || colorForSeriesIndex(i)} />
                      ))}
                    </Pie>
                    <RechartsTooltip contentStyle={tooltipContentStyle} labelStyle={tooltipLabelStyle} cursor={tooltipCursor} />
                    <Legend wrapperStyle={legendTextStyle} />
                  </PieChart>
                </ChartCard>
              </Grid>
              <Grid item xs={12} md={7}>
                <ChartCard title="Stock Value by Category" subtitle="Top 6 categories" cardBg={cardBg}>
                  <BarChart data={categoryData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                    <XAxis dataKey="name" tick={axisStyle} {...rotatedAxisProps} />
                    <YAxis tick={axisStyle} width={70} tickFormatter={(v) => fmtNum(v)} />
                    <RechartsTooltip contentStyle={tooltipContentStyle} labelStyle={tooltipLabelStyle} cursor={tooltipCursor} formatter={(v: number) => fmt(v, sym)} />
                    <Bar dataKey="Value" radius={[4, 4, 0, 0]} maxBarSize={48}>
                      {categoryData.map((_, i) => <Cell key={i} fill={colorForSeriesIndex(i)} />)}
                    </Bar>
                  </BarChart>
                </ChartCard>
              </Grid>
            </Grid>
          );
        })()}

        <Typography variant="h6" gutterBottom>Products</Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, bgcolor: cardBg, backgroundImage: 'none', borderRadius: 1.5 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell><TableCell>SKU</TableCell><TableCell>Category</TableCell>
                <TableCell align="right">Stock</TableCell><TableCell align="right">Value</TableCell><TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.products.slice(0, 50).map((p: any) => (
                <TableRow key={p.productId}>
                  <TableCell>
                    {p.productName}
                    {p.hasVariants && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {p.variants?.length || 0} variants
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{p.sku}</TableCell>
                  <TableCell>{p.category}</TableCell>
                  <TableCell align="right">{fmtNum(p.totalStock)}</TableCell>
                  <TableCell align="right">{fmt(p.stockValue, sym)}</TableCell>
                  <TableCell>
                    <Chip
                      label={p.status.replace('_', ' ')}
                      size="small"
                      variant="outlined"
                      sx={getSoftChipSx(
                        p.status === 'in_stock' ? 'success' : p.status === 'low_stock' ? 'warning' : 'error',
                        isDark
                      )}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // ── Purchase Orders ────────────────────────────────────────────────────────
  const renderImportOrderReport = () => {
    const data = report.data;
    if (!data.purchaseOrders || data.purchaseOrders.length === 0) {
      return (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>No Purchase Orders Found</Typography>
          <Typography variant="body2" color="text.secondary">No purchase orders match the selected criteria.</Typography>
        </Box>
      );
    }
    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Summary</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} label="Total POs"    value={fmtNum(data.summary.totalPOs)} /></Grid>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} label="Total Amount" value={fmt(data.summary.totalAmount || 0, sym)} /></Grid>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} label="Paid"         value={fmt(data.summary.totalPaid || 0, sym)} /></Grid>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} label="Pending"      value={fmt((data.summary.totalAmount || 0) - (data.summary.totalPaid || 0), sym)} /></Grid>
        </Grid>

        {data.summary.statusBreakdown && Object.keys(data.summary.statusBreakdown).length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>Status Breakdown</Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {Object.entries(data.summary.statusBreakdown).map(([status, count]) => (
                <Chip key={status} label={`${status}: ${count}`} size="small" variant="outlined" sx={getSoftChipSx('default', isDark)} />
              ))}
            </Stack>
          </Box>
        )}

        {data.summary.paymentStatusBreakdown && Object.keys(data.summary.paymentStatusBreakdown).length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>Payment Status</Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {Object.entries(data.summary.paymentStatusBreakdown).map(([status, count]) => (
                <Chip
                  key={status}
                  label={`${status}: ${count}`}
                  size="small"
                  variant="outlined"
                  sx={getSoftChipSx(status === 'Paid' ? 'success' : status === 'Partial' ? 'warning' : 'default', isDark)}
                />
              ))}
            </Stack>
          </Box>
        )}

        {data.summary.topSuppliers?.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>Top Suppliers</Typography>
            <Grid container spacing={2}>
              {data.summary.topSuppliers.slice(0, 5).map((s: any, i: number) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: cardBg, backgroundImage: 'none', borderRadius: 1.5 }}>
                    <Typography variant="body2" fontWeight="medium" gutterBottom>{s.supplierName}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">Orders: {s.poCount}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">Total: {fmt(s.totalAmount, sym)}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {(data.summary.statusBreakdown && Object.keys(data.summary.statusBreakdown).length > 0) || data.summary.topSuppliers?.length > 0 ? (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {data.summary.statusBreakdown && Object.keys(data.summary.statusBreakdown).length > 0 && (
              <Grid item xs={12} md={5}>
                <ChartCard title="Purchase Order Status" cardBg={cardBg}>
                  <PieChart>
                    <Pie
                      data={Object.entries(data.summary.statusBreakdown).map(([name, value]) => ({ name, value }))}
                      dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={2}
                    >
                      {Object.keys(data.summary.statusBreakdown).map((status: string, i: number) => (
                        <Cell key={status} fill={STATUS_CHART_COLORS[status === 'Received' ? 'success' : status === 'Approved' ? 'info' : 'warning'] || colorForSeriesIndex(i)} />
                      ))}
                    </Pie>
                    <RechartsTooltip contentStyle={tooltipContentStyle} labelStyle={tooltipLabelStyle} cursor={tooltipCursor} />
                    <Legend wrapperStyle={legendTextStyle} />
                  </PieChart>
                </ChartCard>
              </Grid>
            )}
            {data.summary.topSuppliers?.length > 0 && (
              <Grid item xs={12} md={7}>
                <ChartCard title="Top Suppliers by Amount" cardBg={cardBg}>
                  <BarChart data={data.summary.topSuppliers.slice(0, 5).map((s: any) => ({ name: s.supplierName, Amount: s.totalAmount }))} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                    <XAxis dataKey="name" tick={axisStyle} {...rotatedAxisProps} />
                    <YAxis tick={axisStyle} width={70} tickFormatter={(v) => fmtNum(v)} />
                    <RechartsTooltip contentStyle={tooltipContentStyle} labelStyle={tooltipLabelStyle} cursor={tooltipCursor} formatter={(v: number) => fmt(v, sym)} />
                    <Bar dataKey="Amount" radius={[4, 4, 0, 0]} maxBarSize={48}>
                      {data.summary.topSuppliers.slice(0, 5).map((_: any, i: number) => <Cell key={i} fill={colorForSeriesIndex(i)} />)}
                    </Bar>
                  </BarChart>
                </ChartCard>
              </Grid>
            )}
          </Grid>
        ) : null}

        <Typography variant="h6" gutterBottom>Purchase Orders</Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, bgcolor: cardBg, backgroundImage: 'none', borderRadius: 1.5 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>PO #</TableCell><TableCell>Supplier</TableCell><TableCell>Order Date</TableCell>
                <TableCell>Exp. Delivery</TableCell><TableCell align="right">Amount</TableCell><TableCell>Status</TableCell><TableCell>Payment</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.purchaseOrders.slice(0, 50).map((po: any) => (
                <TableRow key={po.poId}>
                  <TableCell><Typography variant="body2" fontWeight="medium">{po.poNumber}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{po.supplierName}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{po.orderDate instanceof Date ? po.orderDate.toLocaleDateString() : 'N/A'}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{po.expectedDelivery instanceof Date ? po.expectedDelivery.toLocaleDateString() : 'Not set'}</Typography></TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="medium">{fmt(po.totalAmount || 0, sym)}</Typography>
                    {po.paidAmount > 0 && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Paid: {fmt(po.paidAmount, sym)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={po.status || 'draft'}
                      size="small"
                      variant="outlined"
                      sx={getSoftChipSx(po.status === 'Received' ? 'success' : po.status === 'Approved' ? 'info' : 'warning', isDark)}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={po.paymentStatus || 'pending'}
                      size="small"
                      variant="outlined"
                      sx={getSoftChipSx(po.paymentStatus === 'Paid' ? 'success' : po.paymentStatus === 'Partial' ? 'warning' : 'default', isDark)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {data.purchaseOrders.length > 50 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Showing first 50 of {data.purchaseOrders.length} purchase orders
          </Typography>
        )}
      </Box>
    );
  };

  // ── Low Stock ──────────────────────────────────────────────────────────────
  const renderLowStockReport = () => {
    const data = report.data;
    if (!data.items) return null;
    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Summary</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.amber} label="Low Stock Items"     value={fmtNum(data.summary.totalLowStockItems)} /></Grid>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.red}   label="Out of Stock"        value={fmtNum(data.summary.totalOutOfStockItems)} /></Grid>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.slate} label="Affected Products"   value={fmtNum(data.summary.affectedProducts)} /></Grid>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.violet} label="Affected Show Rooms" value={fmtNum(data.summary.affectedWarehouses)} /></Grid>
        </Grid>

        {data.items.length > 0 && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={5}>
              <ChartCard title="Low Stock vs Out of Stock" cardBg={cardBg}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Low Stock', value: data.summary.totalLowStockItems },
                      { name: 'Out of Stock', value: data.summary.totalOutOfStockItems },
                    ]}
                    dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={2}
                  >
                    <Cell fill={STATUS_CHART_COLORS.warning} />
                    <Cell fill={STATUS_CHART_COLORS.error} />
                  </Pie>
                  <RechartsTooltip contentStyle={tooltipContentStyle} labelStyle={tooltipLabelStyle} cursor={tooltipCursor} />
                  <Legend wrapperStyle={legendTextStyle} />
                </PieChart>
              </ChartCard>
            </Grid>
            <Grid item xs={12} md={7}>
              <ChartCard title="Biggest Shortfalls" subtitle="Top 8 by deficit" cardBg={cardBg}>
                <BarChart
                  data={[...data.items].sort((a: any, b: any) => b.deficit - a.deficit).slice(0, 8).map((it: any) => ({ name: it.productName, Deficit: it.deficit }))}
                  margin={{ top: 8, right: 8, left: 0, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                  <XAxis dataKey="name" tick={axisStyle} {...rotatedAxisProps} />
                  <YAxis tick={axisStyle} width={40} allowDecimals={false} />
                  <RechartsTooltip contentStyle={tooltipContentStyle} labelStyle={tooltipLabelStyle} cursor={tooltipCursor} />
                  <Bar dataKey="Deficit" fill={CHART_PALETTE.red} radius={[4, 4, 0, 0]} maxBarSize={48} />
                </BarChart>
              </ChartCard>
            </Grid>
          </Grid>
        )}

        <Typography variant="h6" gutterBottom>Low Stock Items</Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, bgcolor: cardBg, backgroundImage: 'none', borderRadius: 1.5 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell><TableCell>Show Room</TableCell>
                <TableCell align="right">Current</TableCell><TableCell align="right">Min</TableCell>
                <TableCell align="right">Deficit</TableCell><TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.items.map((item: any, i: number) => (
                <TableRow key={i}>
                  <TableCell>
                    {item.productName}
                    {item.variantName && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {item.variantName}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{item.warehouseName}</TableCell>
                  <TableCell align="right">{fmtNum(item.currentStock)}</TableCell>
                  <TableCell align="right">{fmtNum(item.minStock)}</TableCell>
                  <TableCell align="right">{fmtNum(item.deficit)}</TableCell>
                  <TableCell>
                    <Chip
                      label={item.status === 'out_of_stock' ? 'Out of Stock' : 'Low Stock'}
                      size="small"
                      variant="outlined"
                      sx={getSoftChipSx(item.status === 'out_of_stock' ? 'error' : 'warning', isDark)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // ── Warehouse Performance ──────────────────────────────────────────────────
  const renderWarehousePerformanceReport = () => {
    const data = report.data;
    if (!data.warehouses) return null;
    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Summary</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.slate} label="Total Show Rooms" value={fmtNum(data.summary.totalWarehouses)} /></Grid>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.teal}  label="Avg Utilization"  value={`${data.summary.averageUtilization}%`} /></Grid>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.green} label="Most Utilized"    value={<Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 700 }}>{data.summary.mostUtilizedWarehouse}</Typography>} /></Grid>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.amber} label="Least Utilized"   value={<Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 700 }}>{data.summary.leastUtilizedWarehouse}</Typography>} /></Grid>
        </Grid>

        {data.warehouses.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <ChartCard title="Capacity Utilization by Show Room" subtitle="Percentage of capacity in use" cardBg={cardBg} height={Math.max(240, data.warehouses.length * 36)}>
              <BarChart
                data={data.warehouses.map((w: any) => ({ name: w.warehouseName, Utilization: w.utilizationRate }))}
                layout="vertical"
                margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} horizontal={false} />
                <XAxis type="number" tick={axisStyle} unit="%" domain={[0, 100]} />
                <YAxis dataKey="name" type="category" width={140} tick={axisStyle} />
                <RechartsTooltip contentStyle={tooltipContentStyle} labelStyle={tooltipLabelStyle} cursor={tooltipCursor} formatter={(v: number) => `${v}%`} />
                <Bar dataKey="Utilization" radius={[0, 4, 4, 0]} maxBarSize={22}>
                  {data.warehouses.map((w: any, i: number) => (
                    <Cell key={i} fill={w.utilizationRate > 80 ? STATUS_CHART_COLORS.error : w.utilizationRate > 60 ? STATUS_CHART_COLORS.warning : STATUS_CHART_COLORS.success} />
                  ))}
                </Bar>
              </BarChart>
            </ChartCard>
          </Box>
        )}

        <Typography variant="h6" gutterBottom>Show Rooms</Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, bgcolor: cardBg, backgroundImage: 'none', borderRadius: 1.5 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Show Room</TableCell><TableCell>Code</TableCell>
                <TableCell align="right">Products</TableCell><TableCell align="right">Stock</TableCell>
                <TableCell align="right">Capacity Used</TableCell><TableCell align="right">Utilization</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.warehouses.map((w: any) => (
                <TableRow key={w.warehouseId}>
                  <TableCell>
                    {w.warehouseName}
                    {w.manager && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Manager: {w.manager}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{w.code}</TableCell>
                  <TableCell align="right">{fmtNum(w.totalProducts)}</TableCell>
                  <TableCell align="right">{fmtNum(w.totalStock)}</TableCell>
                  <TableCell align="right">{fmtNum(w.capacityUsed)}/{fmtNum(w.capacityTotal)}</TableCell>
                  <TableCell align="right">
                    <Chip
                      label={`${w.utilizationRate}%`}
                      size="small"
                      variant="outlined"
                      sx={getSoftChipSx(w.utilizationRate > 80 ? 'error' : w.utilizationRate > 60 ? 'warning' : 'success', isDark)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // ── Product Performance ────────────────────────────────────────────────────
  const renderProductPerformanceReport = () => {
    const data = report.data;
    if (!data.products) return null;
    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Summary</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.slate} label="Total Products" value={fmtNum(data.summary.totalProducts)} /></Grid>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.green} label="Total Revenue"  value={fmt(data.summary.totalRevenue, sym)} /></Grid>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.teal}  label="Best Seller"    value={<Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 700 }}>{data.summary.bestSellingProduct}</Typography>} /></Grid>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.violet} label="Avg Turnover"   value={data.summary.averageStockTurnover} /></Grid>
        </Grid>

        {data.products.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <ChartCard title="Top Models by Revenue" subtitle="Top 8 by revenue" cardBg={cardBg}>
              <BarChart
                data={[...data.products].sort((a: any, b: any) => b.revenue - a.revenue).slice(0, 8).map((p: any) => ({ name: p.productName, Revenue: p.revenue }))}
                margin={{ top: 8, right: 8, left: 0, bottom: 8 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                <XAxis dataKey="name" tick={axisStyle} {...rotatedAxisProps} />
                <YAxis tick={axisStyle} width={70} tickFormatter={(v) => fmtNum(v)} />
                <RechartsTooltip contentStyle={tooltipContentStyle} labelStyle={tooltipLabelStyle} cursor={tooltipCursor} formatter={(v: number) => fmt(v, sym)} />
                <Bar dataKey="Revenue" radius={[4, 4, 0, 0]} maxBarSize={48}>
                  {[...data.products].sort((a: any, b: any) => b.revenue - a.revenue).slice(0, 8).map((_: any, i: number) => (
                    <Cell key={i} fill={colorForSeriesIndex(i)} />
                  ))}
                </Bar>
              </BarChart>
            </ChartCard>
          </Box>
        )}

        <Typography variant="h6" gutterBottom>Products</Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, bgcolor: cardBg, backgroundImage: 'none', borderRadius: 1.5 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell><TableCell>Category</TableCell>
                <TableCell align="right">Sold</TableCell><TableCell align="right">Revenue</TableCell>
                <TableCell align="right">Avg Price</TableCell><TableCell align="right">Turnover</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.products.slice(0, 50).map((p: any) => (
                <TableRow key={p.productId}>
                  <TableCell>
                    {p.productName}
                    {p.isFeatured && (
                      <Chip
                        label="Featured"
                        size="small"
                        variant="outlined"
                        sx={{ ml: 1, ...getSoftChipSx('primary', isDark) }}
                      />
                    )}
                  </TableCell>
                  <TableCell>{p.category}</TableCell>
                  <TableCell align="right">{fmtNum(p.totalSold)}</TableCell>
                  <TableCell align="right">{fmt(p.revenue, sym)}</TableCell>
                  <TableCell align="right">{fmt(p.averagePrice, sym)}</TableCell>
                  <TableCell align="right">{p.stockTurnover}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // ── Revenue ────────────────────────────────────────────────────────────────
  const renderRevenueReport = () => {
    const data = report.data;
    if (!data.dailyRevenue) return null;
    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Summary</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.green} label="Total Revenue"     value={fmt(data.summary.totalRevenue, sym)} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.teal}  label="Net Revenue"       value={fmt(data.summary.totalNetRevenue, sym)} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.slate} label="Total Orders"      value={fmtNum(data.summary.totalOrders)} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.blue}  label="Avg Order Value"   value={fmt(data.summary.averageOrderValue, sym)} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.violet} label="Avg Daily Revenue" value={fmt(data.summary.averageDailyRevenue, sym)} /></Grid>
          <Grid item xs={6} sm={4} md={3}>
            <SummaryCard
              cardBg={cardBg}
              accentColor={data.summary.growthRate >= 0 ? CHART_PALETTE.green : CHART_PALETTE.red}
              label="Growth Rate"
              value={
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography sx={{ color: data.summary.growthRate >= 0 ? 'success.main' : 'error.main', fontWeight: 700 }}>
                    {data.summary.growthRate >= 0 ? '+' : ''}{data.summary.growthRate.toFixed(2)}%
                  </Typography>
                  {data.summary.growthRate >= 0
                    ? <TrendingUpIcon color="success" fontSize="small" />
                    : <TrendingDownIcon color="error" fontSize="small" />}
                </Stack>
              }
            />
          </Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.sage} label="Highest Revenue Day" value={<Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 700 }}>{data.summary.highestRevenueDay}</Typography>} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.amber} label="Total Discount"      value={fmt(data.summary.totalDiscount, sym)} /></Grid>
        </Grid>

        {data.dailyRevenue.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <ChartCard title="Revenue Trend" subtitle="Daily gross vs. net revenue" cardBg={cardBg} height={300}>
              <LineChart
                data={data.dailyRevenue.map((d: any) => ({
                  date: d.date instanceof Date ? d.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'N/A',
                  Revenue: d.totalRevenue,
                  'Net Revenue': d.netRevenue,
                }))}
                margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                <XAxis dataKey="date" tick={axisStyle} />
                <YAxis tick={axisStyle} width={70} tickFormatter={(v) => fmtNum(v)} />
                <RechartsTooltip contentStyle={tooltipContentStyle} labelStyle={tooltipLabelStyle} cursor={tooltipCursor} formatter={(v: number) => fmt(v, sym)} />
                <Legend wrapperStyle={legendTextStyle} />
                <Line type="monotone" dataKey="Revenue" stroke={CHART_PALETTE.green} strokeWidth={2.5} dot={{ r: 3, fill: CHART_PALETTE.green, strokeWidth: 0 }} activeDot={{ r: 5 }} />
                <Line type="monotone" dataKey="Net Revenue" stroke={CHART_PALETTE.blue} strokeWidth={2.5} dot={{ r: 3, fill: CHART_PALETTE.blue, strokeWidth: 0 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ChartCard>
          </Box>
        )}

        {data.topProducts?.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>Top Products by Revenue</Typography>
            <Grid container spacing={2}>
              {data.topProducts.slice(0, 5).map((p: any, i: number) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: cardBg, backgroundImage: 'none', borderRadius: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="body2" fontWeight="medium">{p.productName}</Typography>
                      <Chip
                        label={`${p.percentageOfTotal.toFixed(1)}%`}
                        size="small"
                        variant="outlined"
                        sx={getSoftChipSx('primary', isDark)}
                      />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block">Revenue: {fmt(p.revenue, sym)}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">Units Sold: {fmtNum(p.totalSold)}</Typography>
                    <LinearProgress variant="determinate" value={p.percentageOfTotal} sx={{ mt: 1, height: 6, borderRadius: 1 }} />
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {data.topCategories?.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>Top Categories by Revenue</Typography>
            <Grid container spacing={2}>
              {data.topCategories.slice(0, 5).map((c: any, i: number) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: cardBg, backgroundImage: 'none', borderRadius: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="body2" fontWeight="medium">{c.categoryName}</Typography>
                      <Chip
                        label={`${c.percentageOfTotal.toFixed(1)}%`}
                        size="small"
                        variant="outlined"
                        sx={getSoftChipSx('secondary', isDark)}
                      />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block">Revenue: {fmt(c.revenue, sym)}</Typography>
                    <LinearProgress variant="determinate" value={c.percentageOfTotal} color="secondary" sx={{ mt: 1, height: 6, borderRadius: 1 }} />
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {data.topCategories?.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <ChartCard title="Revenue Share by Category" cardBg={cardBg}>
              <PieChart>
                <Pie
                  data={data.topCategories.slice(0, 6).map((c: any) => ({ name: c.categoryName, value: c.revenue }))}
                  dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={2}
                >
                  {data.topCategories.slice(0, 6).map((_: any, i: number) => <Cell key={i} fill={colorForSeriesIndex(i)} />)}
                </Pie>
                <RechartsTooltip contentStyle={tooltipContentStyle} labelStyle={tooltipLabelStyle} cursor={tooltipCursor} formatter={(v: number) => fmt(v, sym)} />
                <Legend wrapperStyle={legendTextStyle} />
              </PieChart>
            </ChartCard>
          </Box>
        )}

        <Typography variant="h6" gutterBottom>Daily Revenue</Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, bgcolor: cardBg, backgroundImage: 'none', borderRadius: 1.5 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell><TableCell align="right">Orders</TableCell>
                <TableCell align="right">Revenue</TableCell><TableCell align="right">Net Revenue</TableCell>
                <TableCell align="right">Avg Order</TableCell><TableCell align="right">Tax</TableCell><TableCell align="right">Shipping</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.dailyRevenue.map((day: any, i: number) => (
                <TableRow key={i}>
                  <TableCell>{day.date instanceof Date ? day.date.toLocaleDateString() : 'N/A'}</TableCell>
                  <TableCell align="right">{fmtNum(day.totalOrders)}</TableCell>
                  <TableCell align="right"><Typography variant="body2" fontWeight="medium">{fmt(day.totalRevenue, sym)}</Typography></TableCell>
                  <TableCell align="right">{fmt(day.netRevenue, sym)}</TableCell>
                  <TableCell align="right">{fmt(day.averageOrderValue, sym)}</TableCell>
                  <TableCell align="right">{fmt(day.totalTax, sym)}</TableCell>
                  <TableCell align="right">{fmt(day.totalShipping, sym)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // ── Returns & Cancellations ────────────────────────────────────────────────
  const renderReturnsCancellationsReport = () => {
    const data = report.data;
    if (!data.orders) return null;
    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Summary</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.red}   label="Cancelled"       value={fmtNum(data.summary.totalCancelled)} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.amber} label="Gone Cold (Lost)" value={fmtNum(data.summary.totalRefunded)} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.slate} label="Reservations Reviewed" value={fmtNum(data.summary.totalOrders)} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.violet} label="Lost Revenue"    value={fmt(data.summary.totalLostRevenue, sym)} /></Grid>
          <Grid item xs={6} sm={4} md={3}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: cardBg,
                backgroundImage: 'none',
                borderRadius: 1.5,
                height: '100%',
              }}
            >
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>Rate</Typography>
              <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 700 }}>Cancelled/Lost: {data.summary.cancellationRate}%</Typography>
            </Paper>
          </Grid>
        </Grid>

        {data.orders.length > 0 && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={5}>
              <ChartCard title="Reservation Outcome" cardBg={cardBg}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Returned', value: data.summary.totalReturned || 0 },
                      { name: 'Cancelled', value: data.summary.totalCancelled || 0 },
                      { name: 'Gone Cold', value: data.summary.totalRefunded || 0 },
                    ]}
                    dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={2}
                  >
                    <Cell fill={STATUS_CHART_COLORS.warning} />
                    <Cell fill={STATUS_CHART_COLORS.error} />
                    <Cell fill={STATUS_CHART_COLORS.secondary} />
                  </Pie>
                  <RechartsTooltip contentStyle={tooltipContentStyle} labelStyle={tooltipLabelStyle} cursor={tooltipCursor} />
                  <Legend wrapperStyle={legendTextStyle} />
                </PieChart>
              </ChartCard>
            </Grid>
            {data.summary.topReturnedProducts?.length > 0 && (
              <Grid item xs={12} md={7}>
                <ChartCard title="Deals Lost by Model" subtitle="Top 5 by quantity" cardBg={cardBg}>
                  <BarChart data={data.summary.topReturnedProducts.slice(0, 5).map((p: any) => ({ name: p.productName, 'Deals Lost': p.quantity }))} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                    <XAxis dataKey="name" tick={axisStyle} {...rotatedAxisProps} />
                    <YAxis tick={axisStyle} width={40} allowDecimals={false} />
                    <RechartsTooltip contentStyle={tooltipContentStyle} labelStyle={tooltipLabelStyle} cursor={tooltipCursor} />
                    <Bar dataKey="Deals Lost" fill={CHART_PALETTE.red} radius={[4, 4, 0, 0]} maxBarSize={48} />
                  </BarChart>
                </ChartCard>
              </Grid>
            )}
          </Grid>
        )}

        {data.summary.topReturnedProducts?.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>Most Cancelled / Lost Models</Typography>
            <Grid container spacing={2}>
              {data.summary.topReturnedProducts.slice(0, 5).map((p: any, i: number) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: cardBg, backgroundImage: 'none', borderRadius: 1.5 }}>
                    <Typography variant="body2" fontWeight="medium" gutterBottom>{p.productName}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">Deals Lost: {fmtNum(p.quantity)}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">Lost Revenue: {fmt(p.revenue, sym)}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        <Typography variant="h6" gutterBottom>Cancelled &amp; Lost Reservations</Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, bgcolor: cardBg, backgroundImage: 'none', borderRadius: 1.5 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Vehicle</TableCell><TableCell>Customer</TableCell><TableCell>Date</TableCell>
                <TableCell align="right">Amount</TableCell><TableCell>Status</TableCell>
                <TableCell>Payment</TableCell><TableCell>Reason</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.orders.slice(0, 50).map((o: any) => (
                <TableRow key={o.orderId}>
                  <TableCell>{o.orderNumber}</TableCell>
                  <TableCell>{o.customerName}</TableCell>
                  <TableCell>{o.orderDate instanceof Date ? o.orderDate.toLocaleDateString() : 'N/A'}</TableCell>
                  <TableCell align="right">{fmt(o.totalAmount, sym)}</TableCell>
                  <TableCell>
                    <Chip
                      label={o.status}
                      size="small"
                      variant="outlined"
                      sx={getSoftChipSx(o.status === 'Returned' ? 'warning' : o.status === 'Cancelled' ? 'error' : 'secondary', isDark)}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip label={o.paymentMethod} size="small" variant="outlined" sx={getSoftChipSx('primary', isDark)} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">{o.reason || '—'}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {data.orders.length > 50 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Showing first 50 of {data.orders.length} orders
          </Typography>
        )}
      </Box>
    );
  };

  // ── Profit & Loss ──────────────────────────────────────────────────────────
  const renderProfitLossReport = () => {
    const data = report.data;
    if (!data.summary) return null;
    const s = data.summary;
    const netPositive = s.netProfit >= 0;
    return (
      <Box>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          Period: {s.periodDays} day{s.periodDays === 1 ? '' : 's'} · {fmtNum(s.totalOrders)} delivered order{s.totalOrders === 1 ? '' : 's'}.
          Cost of goods sold uses the weighted-average cost snapshotted at the moment each sale deducted stock — orders
          placed before that tracking existed may show $0 cost.
        </Typography>

        <Typography variant="h6" gutterBottom>Summary</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.green} label="Revenue" value={fmt(s.revenue, sym)} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.red}   label="COGS" value={fmt(s.cogs, sym)} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.teal}  label="Gross Profit" value={fmt(s.grossProfit, sym)} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.teal}  label="Gross Margin" value={`${s.grossMarginPercent.toFixed(1)}%`} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.amber} label="Operating Costs" value={fmt(s.operatingCosts, sym)} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.tan}   label="  — One-Time" value={fmt(s.operatingCostsOneTime, sym)} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.mauve} label="  — Recurring (prorated)" value={fmt(s.operatingCostsRecurring, sym)} /></Grid>
          <Grid item xs={6} sm={4} md={3}>
            <SummaryCard
              cardBg={cardBg}
              accentColor={netPositive ? CHART_PALETTE.green : CHART_PALETTE.red}
              label="Net Profit"
              value={
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography sx={{ color: netPositive ? 'success.main' : 'error.main', fontWeight: 700 }}>
                    {fmt(s.netProfit, sym)}
                  </Typography>
                  {netPositive
                    ? <TrendingUpIcon color="success" fontSize="small" />
                    : <TrendingDownIcon color="error" fontSize="small" />}
                </Stack>
              }
            />
          </Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.slate} label="Net Margin" value={`${s.netMarginPercent.toFixed(1)}%`} /></Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={data.costBreakdown?.length > 0 ? 7 : 12}>
            <ChartCard title="Revenue, Cost &amp; Profit" cardBg={cardBg}>
              <BarChart
                data={[
                  { name: 'Revenue', Amount: s.revenue, fill: CHART_PALETTE.green },
                  { name: 'COGS', Amount: s.cogs, fill: CHART_PALETTE.red },
                  { name: 'Gross Profit', Amount: s.grossProfit, fill: CHART_PALETTE.teal },
                  { name: 'Op. Costs', Amount: s.operatingCosts, fill: CHART_PALETTE.amber },
                  { name: 'Net Profit', Amount: s.netProfit, fill: netPositive ? CHART_PALETTE.green : CHART_PALETTE.red },
                ]}
                margin={{ top: 8, right: 8, left: 0, bottom: 8 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                <XAxis dataKey="name" tick={axisStyle} {...rotatedAxisProps} />
                <YAxis tick={axisStyle} width={70} tickFormatter={(v) => fmtNum(v)} />
                <RechartsTooltip contentStyle={tooltipContentStyle} labelStyle={tooltipLabelStyle} cursor={tooltipCursor} formatter={(v: number) => fmt(v, sym)} />
                <Bar dataKey="Amount" radius={[4, 4, 0, 0]} maxBarSize={56}>
                  {[CHART_PALETTE.green, CHART_PALETTE.red, CHART_PALETTE.teal, CHART_PALETTE.amber, netPositive ? CHART_PALETTE.green : CHART_PALETTE.red].map((c, i) => (
                    <Cell key={i} fill={c} />
                  ))}
                </Bar>
              </BarChart>
            </ChartCard>
          </Grid>
          {data.costBreakdown?.length > 0 && (
            <Grid item xs={12} md={5}>
              <ChartCard title="Operating Costs by Category" cardBg={cardBg}>
                <PieChart>
                  <Pie
                    data={data.costBreakdown.map((c: any) => ({ name: c.category, value: c.amount }))}
                    dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={2}
                  >
                    {data.costBreakdown.map((_: any, i: number) => <Cell key={i} fill={colorForSeriesIndex(i)} />)}
                  </Pie>
                  <RechartsTooltip contentStyle={tooltipContentStyle} labelStyle={tooltipLabelStyle} cursor={tooltipCursor} formatter={(v: number) => fmt(v, sym)} />
                  <Legend wrapperStyle={legendTextStyle} />
                </PieChart>
              </ChartCard>
            </Grid>
          )}
        </Grid>

        {data.costBreakdown?.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>Operating Costs by Category</Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300, bgcolor: cardBg, backgroundImage: 'none', borderRadius: 1.5 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">One-Time</TableCell>
                    <TableCell align="right">Recurring</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Entries</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.costBreakdown.map((c: any, i: number) => (
                    <TableRow key={i}>
                      <TableCell>{c.category}</TableCell>
                      <TableCell align="right">{fmt(c.oneTimeAmount, sym)}</TableCell>
                      <TableCell align="right">{fmt(c.recurringAmount, sym)}</TableCell>
                      <TableCell align="right"><Typography variant="body2" fontWeight="medium">{fmt(c.amount, sym)}</Typography></TableCell>
                      <TableCell align="right">{fmtNum(c.count)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        <Typography variant="h6" gutterBottom>Revenue &amp; Profit by Product</Typography>
        {data.revenueByProduct?.length > 0 ? (
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, bgcolor: cardBg, backgroundImage: 'none', borderRadius: 1.5 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell align="right">Qty Sold</TableCell>
                  <TableCell align="right">Revenue</TableCell>
                  <TableCell align="right">COGS</TableCell>
                  <TableCell align="right">Gross Profit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.revenueByProduct.slice(0, 50).map((p: any, i: number) => (
                  <TableRow key={i}>
                    <TableCell>{p.productName}</TableCell>
                    <TableCell align="right">{fmtNum(p.quantitySold)}</TableCell>
                    <TableCell align="right">{fmt(p.revenue, sym)}</TableCell>
                    <TableCell align="right">{fmt(p.cogs, sym)}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium" sx={{ color: p.grossProfit >= 0 ? 'success.main' : 'error.main' }}>
                        {fmt(p.grossProfit, sym)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2" color="text.secondary">No delivered orders in this date range.</Typography>
        )}
        {data.revenueByProduct?.length > 50 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Showing first 50 of {data.revenueByProduct.length} products
          </Typography>
        )}
      </Box>
    );
  };

  // ── Router ─────────────────────────────────────────────────────────────────
  const renderReportData = () => {
    switch (report.reportType) {
      case 'sales':                 return renderSalesReport();
      case 'inventory':             return renderInventoryReport();
      case 'purchase_orders':       return renderImportOrderReport();
      case 'low_stock':             return renderLowStockReport();
      case 'warehouse_performance': return renderWarehousePerformanceReport();
      case 'product_performance':   return renderProductPerformanceReport();
      case 'revenue':               return renderRevenueReport();
      case 'returns_cancellations': return renderReturnsCancellationsReport();
      case 'profit_loss':           return renderProfitLossReport();
      default: return <Typography>Report type not supported</Typography>;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: dialogBg,
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundImage: isDark
            ? `linear-gradient(120deg, ${alpha(CHART_PALETTE.slate, 0.22)} 0%, ${alpha(CHART_PALETTE.teal, 0.14)} 100%)`
            : `linear-gradient(120deg, ${alpha(CHART_PALETTE.slate, 0.10)} 0%, ${alpha(CHART_PALETTE.teal, 0.06)} 100%)`,
          bgcolor: dialogBg,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 42, height: 42, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: alpha(CHART_PALETTE.slate, isDark ? 0.28 : 0.14),
                color: CHART_PALETTE.slate,
                flexShrink: 0,
              }}
            >
              <AssessmentIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, wordBreak: 'break-word' }}>{report.reportName}</Typography>
              <Typography variant="caption" color="text.secondary">
                {report.reportType.replace(/_/g, ' ')} · {report.generatedAt?.toLocaleDateString()}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          bgcolor: dialogBg,
          backgroundImage: 'none',
        }}
      >
        {/* Meta */}
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.slate} label="Report Type" value={<Chip label={report.reportType.replace(/_/g, ' ')} size="small" variant="outlined" sx={getSoftChipSx('primary', isDark)} />} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <SummaryCard
                cardBg={cardBg}
                accentColor={CHART_PALETTE.teal}
                label="Generated At"
                value={
                  <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 700 }}>
                    {report.generatedAt?.toLocaleDateString()}
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 400 }}>
                      {report.generatedAt?.toLocaleTimeString()}
                    </Typography>
                  </Typography>
                }
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.violet} label="Generated By" value={<Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 700 }} noWrap>{report.generatedByName}</Typography>} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <SummaryCard cardBg={cardBg} accentColor={CHART_PALETTE.green} label="Total Records" value={fmtNum(report.summary?.totalRecords || 0)} />
            </Grid>
            {report.description && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">Description</Typography>
                <Typography variant="body1" sx={{ color: 'text.primary' }}>{report.description}</Typography>
              </Grid>
            )}
          </Grid>
        </Box>

        <Divider sx={{ mb: 2 }} />
        {renderReportData()}
      </DialogContent>

      <DialogActions
        sx={{
          bgcolor: dialogBg,
          backgroundImage: 'none',
        }}
      >
        <Button onClick={onClose}>Close</Button>
        <Button onClick={handleDownloadPDF} variant="contained" startIcon={<PdfIcon />}>Download PDF</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReportDetailsDialog;

// import React from 'react';
// import {
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   Button,
//   Box,
//   Typography,
//   Grid,
//   Chip,
//   Divider,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Paper,
//   IconButton,
//   Stack,
//   LinearProgress,
// } from '@mui/material';
// import {
//   Close as CloseIcon,
//   PictureAsPdf as PdfIcon,
//   TrendingUp as TrendingUpIcon,
//   TrendingDown as TrendingDownIcon,
// } from '@mui/icons-material';
// import { Report } from './Report';
// import { ReportPDFGenerator } from './reportPDFGenerator';
// import { useCompany } from '../../../contexts/CompanyContext';
// import { getCurrencySymbol } from '../../../util/productUtils';

// interface ReportDetailsDialogProps {
//   open: boolean;
//   report: Report;
//   onClose: () => void;
// }

// const ReportDetailsDialog: React.FC<ReportDetailsDialogProps> = ({
//   open,
//   report,
//   onClose,
// }) => {
//   const { companyProfile } = useCompany();
//   const companyCurrency = companyProfile?.currency || 'LKR';
//   const currencySymbol = getCurrencySymbol(companyCurrency);

//   const handleDownloadPDF = async () => {
//     try {
//       await ReportPDFGenerator.generatePDF(report, companyCurrency);
//     } catch (error) {
//       console.error('Error downloading PDF:', error);
//       alert('Failed to download PDF');
//     }
//   };

//   const renderSalesReport = () => {
//     const data = report.data;
//     if (!data.orders) return null;

//     return (
//       <Box>
//         <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
//           Summary
//         </Typography>
//         <Grid container spacing={2} sx={{ mb: 3 }}>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Total Orders</Typography>
//               <Typography variant="h6">{data.summary.totalOrders}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Total Revenue</Typography>
//               <Typography variant="h6">{currencySymbol} {data.summary.totalRevenue.toFixed(2)}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Avg Order Value</Typography>
//               <Typography variant="h6">{currencySymbol} {data.summary.averageOrderValue.toFixed(2)}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Total Tax</Typography>
//               <Typography variant="h6">{currencySymbol} {data.summary.totalTax.toFixed(2)}</Typography>
//             </Paper>
//           </Grid>
//         </Grid>

//         {/* Top Products */}
//         {data.summary.topProducts && data.summary.topProducts.length > 0 && (
//           <Box sx={{ mb: 3 }}>
//             <Typography variant="subtitle1" gutterBottom>
//               Top Products
//             </Typography>
//             <Grid container spacing={2}>
//               {data.summary.topProducts.slice(0, 5).map((product: any, index: number) => (
//                 <Grid item xs={12} sm={6} md={4} key={index}>
//                   <Paper sx={{ p: 2 }}>
//                     <Typography variant="body2" fontWeight="medium" gutterBottom>
//                       {product.productName}
//                     </Typography>
//                     <Typography variant="caption" color="text.secondary" display="block">
//                       Quantity: {product.quantity}
//                     </Typography>
//                     <Typography variant="caption" color="text.secondary" display="block">
//                       Revenue: {currencySymbol} {product.revenue.toFixed(2)}
//                     </Typography>
//                   </Paper>
//                 </Grid>
//               ))}
//             </Grid>
//           </Box>
//         )}

//         <Typography variant="h6" gutterBottom>
//           Orders
//         </Typography>
//         <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
//           <Table size="small" stickyHeader>
//             <TableHead>
//               <TableRow>
//                 <TableCell>Order #</TableCell>
//                 <TableCell>Customer</TableCell>
//                 <TableCell>Date</TableCell>
//                 <TableCell align="right">Amount</TableCell>
//                 <TableCell>Payment</TableCell>
//                 <TableCell>Status</TableCell>
//               </TableRow>
//             </TableHead>
//             <TableBody>
//               {data.orders.slice(0, 50).map((order: any) => (
//                 <TableRow key={order.orderId}>
//                   <TableCell>{order.orderNumber}</TableCell>
//                   <TableCell>{order.customerName}</TableCell>
//                   <TableCell>
//                     {order.orderDate instanceof Date 
//                       ? order.orderDate.toLocaleDateString() 
//                       : 'N/A'}
//                   </TableCell>
//                   <TableCell align="right">{currencySymbol} {order.totalAmount.toFixed(2)}</TableCell>
//                   <TableCell>
//                     <Chip 
//                       label={order.paymentMethod} 
//                       size="small" 
//                       variant="outlined"
//                     />
//                   </TableCell>
//                   <TableCell>
//                     <Chip label={order.status} size="small" />
//                   </TableCell>
//                 </TableRow>
//               ))}
//             </TableBody>
//           </Table>
//         </TableContainer>
//       </Box>
//     );
//   };

//   const renderInventoryReport = () => {
//     const data = report.data;
//     if (!data.products) return null;

//     return (
//       <Box>
//         <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
//           Summary
//         </Typography>
//         <Grid container spacing={2} sx={{ mb: 3 }}>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Total Products</Typography>
//               <Typography variant="h6">{data.summary.totalProducts}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Total Stock</Typography>
//               <Typography variant="h6">{data.summary.totalStock}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Stock Value</Typography>
//               <Typography variant="h6">{currencySymbol} {data.summary.totalValue.toFixed(2)}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Low Stock</Typography>
//               <Typography variant="h6" color="warning.main">{data.summary.lowStockItems}</Typography>
//             </Paper>
//           </Grid>
//         </Grid>

//         <Typography variant="h6" gutterBottom>
//           Products
//         </Typography>
//         <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
//           <Table size="small" stickyHeader>
//             <TableHead>
//               <TableRow>
//                 <TableCell>Product</TableCell>
//                 <TableCell>SKU</TableCell>
//                 <TableCell>Category</TableCell>
//                 <TableCell align="right">Stock</TableCell>
//                 <TableCell align="right">Value</TableCell>
//                 <TableCell>Status</TableCell>
//               </TableRow>
//             </TableHead>
//             <TableBody>
//               {data.products.slice(0, 50).map((product: any) => (
//                 <TableRow key={product.productId}>
//                   <TableCell>
//                     {product.productName}
//                     {product.hasVariants && (
//                       <Typography variant="caption" color="text.secondary" display="block">
//                         {product.variants?.length || 0} variants
//                       </Typography>
//                     )}
//                   </TableCell>
//                   <TableCell>{product.sku}</TableCell>
//                   <TableCell>{product.category}</TableCell>
//                   <TableCell align="right">{product.totalStock}</TableCell>
//                   <TableCell align="right">{currencySymbol} {product.stockValue.toFixed(2)}</TableCell>
//                   <TableCell>
//                     <Chip 
//                       label={product.status.replace('_', ' ')} 
//                       size="small"
//                       color={product.status === 'in_stock' ? 'success' : product.status === 'low_stock' ? 'warning' : 'error'}
//                     />
//                   </TableCell>
//                 </TableRow>
//               ))}
//             </TableBody>
//           </Table>
//         </TableContainer>
//       </Box>
//     );
//   };

//   const renderPurchaseOrderReport = () => {
//     const data = report.data;
//     if (!data.purchaseOrders || data.purchaseOrders.length === 0) {
//       return (
//         <Box sx={{ py: 4, textAlign: 'center' }}>
//           <Typography variant="h6" color="text.secondary" gutterBottom>
//             No Purchase Orders Found
//           </Typography>
//           <Typography variant="body2" color="text.secondary">
//             No purchase orders match the selected criteria.
//           </Typography>
//         </Box>
//       );
//     }

//     return (
//       <Box>
//         <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
//           Summary
//         </Typography>
//         <Grid container spacing={2} sx={{ mb: 3 }}>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Total POs</Typography>
//               <Typography variant="h6">{data.summary.totalPOs}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Total Amount</Typography>
//               <Typography variant="h6">{currencySymbol} {data.summary.totalAmount?.toFixed(2) || '0.00'}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Paid</Typography>
//               <Typography variant="h6" color="success.main">
//                 {currencySymbol} {data.summary.totalPaid?.toFixed(2) || '0.00'}
//               </Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Pending</Typography>
//               <Typography variant="h6" color="warning.main">
//                 {currencySymbol} {((data.summary.totalAmount || 0) - (data.summary.totalPaid || 0)).toFixed(2)}
//               </Typography>
//             </Paper>
//           </Grid>
//         </Grid>

//         {/* Status Breakdown */}
//         {data.summary.statusBreakdown && Object.keys(data.summary.statusBreakdown).length > 0 && (
//           <Box sx={{ mb: 3 }}>
//             <Typography variant="subtitle1" gutterBottom>
//               Status Breakdown
//             </Typography>
//             <Grid container spacing={1}>
//               {Object.entries(data.summary.statusBreakdown).map(([status, count]) => (
//                 <Grid item key={status}>
//                   <Chip 
//                     label={`${status}: ${count}`}
//                     size="small"
//                     sx={{ mr: 1, mb: 1 }}
//                   />
//                 </Grid>
//               ))}
//             </Grid>
//           </Box>
//         )}

//         {/* Payment Status Breakdown */}
//         {data.summary.paymentStatusBreakdown && Object.keys(data.summary.paymentStatusBreakdown).length > 0 && (
//           <Box sx={{ mb: 3 }}>
//             <Typography variant="subtitle1" gutterBottom>
//               Payment Status
//             </Typography>
//             <Grid container spacing={1}>
//               {Object.entries(data.summary.paymentStatusBreakdown).map(([status, count]) => (
//                 <Grid item key={status}>
//                   <Chip 
//                     label={`${status}: ${count}`}
//                     size="small"
//                     color={status === 'paid' ? 'success' : status === 'partial' ? 'warning' : 'default'}
//                     sx={{ mr: 1, mb: 1 }}
//                   />
//                 </Grid>
//               ))}
//             </Grid>
//           </Box>
//         )}

//         {/* Top Suppliers */}
//         {data.summary.topSuppliers && data.summary.topSuppliers.length > 0 && (
//           <Box sx={{ mb: 3 }}>
//             <Typography variant="subtitle1" gutterBottom>
//               Top Suppliers
//             </Typography>
//             <Grid container spacing={2}>
//               {data.summary.topSuppliers.slice(0, 5).map((supplier: any, index: number) => (
//                 <Grid item xs={12} sm={6} md={4} key={index}>
//                   <Paper sx={{ p: 2 }}>
//                     <Typography variant="body2" fontWeight="medium" gutterBottom>
//                       {supplier.supplierName}
//                     </Typography>
//                     <Typography variant="caption" color="text.secondary" display="block">
//                       Orders: {supplier.poCount}
//                     </Typography>
//                     <Typography variant="caption" color="text.secondary" display="block">
//                       Total: {currencySymbol} {supplier.totalAmount.toFixed(2)}
//                     </Typography>
//                   </Paper>
//                 </Grid>
//               ))}
//             </Grid>
//           </Box>
//         )}

//         <Typography variant="h6" gutterBottom>
//           Purchase Orders
//         </Typography>
//         <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
//           <Table size="small" stickyHeader>
//             <TableHead>
//               <TableRow>
//                 <TableCell>PO #</TableCell>
//                 <TableCell>Supplier</TableCell>
//                 <TableCell>Order Date</TableCell>
//                 <TableCell>Expected Delivery</TableCell>
//                 <TableCell align="right">Amount</TableCell>
//                 <TableCell>Status</TableCell>
//                 <TableCell>Payment</TableCell>
//               </TableRow>
//             </TableHead>
//             <TableBody>
//               {data.purchaseOrders.slice(0, 50).map((po: any) => (
//                 <TableRow key={po.poId}>
//                   <TableCell>
//                     <Typography variant="body2" fontWeight="medium">
//                       {po.poNumber}
//                     </Typography>
//                   </TableCell>
//                   <TableCell>
//                     <Typography variant="body2">{po.supplierName}</Typography>
//                   </TableCell>
//                   <TableCell>
//                     <Typography variant="body2">
//                       {po.orderDate instanceof Date 
//                         ? po.orderDate.toLocaleDateString() 
//                         : 'N/A'}
//                     </Typography>
//                   </TableCell>
//                   <TableCell>
//                     <Typography variant="body2">
//                       {po.expectedDelivery instanceof Date 
//                         ? po.expectedDelivery.toLocaleDateString() 
//                         : 'Not set'}
//                     </Typography>
//                   </TableCell>
//                   <TableCell align="right">
//                     <Typography variant="body2" fontWeight="medium">
//                       {currencySymbol} {po.totalAmount?.toFixed(2) || '0.00'}
//                     </Typography>
//                     {po.paidAmount > 0 && (
//                       <Typography variant="caption" color="text.secondary" display="block">
//                         Paid: {currencySymbol} {po.paidAmount?.toFixed(2)}
//                       </Typography>
//                     )}
//                   </TableCell>
//                   <TableCell>
//                     <Chip 
//                       label={po.status || 'draft'} 
//                       size="small"
//                       color={
//                         po.status === 'Received' ? 'success' :
//                         po.status === 'Approved' ? 'info' :
//                         po.status === 'Pending' ? 'warning' : 'default'
//                       }
//                     />
//                   </TableCell>
//                   <TableCell>
//                     <Chip 
//                       label={po.paymentStatus || 'pending'} 
//                       size="small"
//                       color={
//                         po.paymentStatus === 'Paid' ? 'success' :
//                         po.paymentStatus === 'Partial' ? 'warning' : 'default'
//                       }
//                       variant="outlined"
//                     />
//                   </TableCell>
//                 </TableRow>
//               ))}
//             </TableBody>
//           </Table>
//         </TableContainer>
        
//         {data.purchaseOrders.length > 50 && (
//           <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
//             Showing first 50 of {data.purchaseOrders.length} purchase orders
//           </Typography>
//         )}
//       </Box>
//     );
//   };

//   const renderLowStockReport = () => {
//     const data = report.data;
//     if (!data.items) return null;

//     return (
//       <Box>
//         <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
//           Summary
//         </Typography>
//         <Grid container spacing={2} sx={{ mb: 3 }}>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Low Stock Items</Typography>
//               <Typography variant="h6" color="warning.main">{data.summary.totalLowStockItems}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Out of Stock</Typography>
//               <Typography variant="h6" color="error.main">{data.summary.totalOutOfStockItems}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Affected Products</Typography>
//               <Typography variant="h6">{data.summary.affectedProducts}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Affected Warehouses</Typography>
//               <Typography variant="h6">{data.summary.affectedWarehouses}</Typography>
//             </Paper>
//           </Grid>
//         </Grid>

//         <Typography variant="h6" gutterBottom>
//           Low Stock Items
//         </Typography>
//         <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
//           <Table size="small" stickyHeader>
//             <TableHead>
//               <TableRow>
//                 <TableCell>Product</TableCell>
//                 <TableCell>Warehouse</TableCell>
//                 <TableCell align="right">Current</TableCell>
//                 <TableCell align="right">Min</TableCell>
//                 <TableCell align="right">Deficit</TableCell>
//                 <TableCell>Status</TableCell>
//               </TableRow>
//             </TableHead>
//             <TableBody>
//               {data.items.map((item: any, index: number) => (
//                 <TableRow key={index}>
//                   <TableCell>
//                     {item.productName}
//                     {item.variantName && (
//                       <Typography variant="caption" color="text.secondary" display="block">
//                         {item.variantName}
//                       </Typography>
//                     )}
//                   </TableCell>
//                   <TableCell>{item.warehouseName}</TableCell>
//                   <TableCell align="right">{item.currentStock}</TableCell>
//                   <TableCell align="right">{item.minStock}</TableCell>
//                   <TableCell align="right">{item.deficit}</TableCell>
//                   <TableCell>
//                     <Chip 
//                       label={item.status === 'out_of_stock' ? 'Out of Stock' : 'Low Stock'} 
//                       size="small"
//                       color={item.status === 'out_of_stock' ? 'error' : 'warning'}
//                     />
//                   </TableCell>
//                 </TableRow>
//               ))}
//             </TableBody>
//           </Table>
//         </TableContainer>
//       </Box>
//     );
//   };

//   const renderWarehousePerformanceReport = () => {
//     const data = report.data;
//     if (!data.warehouses) return null;

//     return (
//       <Box>
//         <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
//           Summary
//         </Typography>
//         <Grid container spacing={2} sx={{ mb: 3 }}>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Total Warehouses</Typography>
//               <Typography variant="h6">{data.summary.totalWarehouses}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Avg Utilization</Typography>
//               <Typography variant="h6">{data.summary.averageUtilization}%</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Most Utilized</Typography>
//               <Typography variant="body2">{data.summary.mostUtilizedWarehouse}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Least Utilized</Typography>
//               <Typography variant="body2">{data.summary.leastUtilizedWarehouse}</Typography>
//             </Paper>
//           </Grid>
//         </Grid>

//         <Typography variant="h6" gutterBottom>
//           Warehouses
//         </Typography>
//         <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
//           <Table size="small" stickyHeader>
//             <TableHead>
//               <TableRow>
//                 <TableCell>Warehouse</TableCell>
//                 <TableCell>Code</TableCell>
//                 <TableCell align="right">Products</TableCell>
//                 <TableCell align="right">Stock</TableCell>
//                 <TableCell align="right">Capacity Used</TableCell>
//                 <TableCell align="right">Utilization</TableCell>
//               </TableRow>
//             </TableHead>
//             <TableBody>
//               {data.warehouses.map((warehouse: any) => (
//                 <TableRow key={warehouse.warehouseId}>
//                   <TableCell>
//                     {warehouse.warehouseName}
//                     {warehouse.manager && (
//                       <Typography variant="caption" color="text.secondary" display="block">
//                         Manager: {warehouse.manager}
//                       </Typography>
//                     )}
//                   </TableCell>
//                   <TableCell>{warehouse.code}</TableCell>
//                   <TableCell align="right">{warehouse.totalProducts}</TableCell>
//                   <TableCell align="right">{warehouse.totalStock}</TableCell>
//                   <TableCell align="right">{warehouse.capacityUsed}/{warehouse.capacityTotal}</TableCell>
//                   <TableCell align="right">
//                     <Chip 
//                       label={`${warehouse.utilizationRate}%`}
//                       size="small"
//                       color={warehouse.utilizationRate > 80 ? 'error' : warehouse.utilizationRate > 60 ? 'warning' : 'success'}
//                     />
//                   </TableCell>
//                 </TableRow>
//               ))}
//             </TableBody>
//           </Table>
//         </TableContainer>
//       </Box>
//     );
//   };

//   const renderProductPerformanceReport = () => {
//     const data = report.data;
//     if (!data.products) return null;

//     return (
//       <Box>
//         <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
//           Summary
//         </Typography>
//         <Grid container spacing={2} sx={{ mb: 3 }}>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Total Products</Typography>
//               <Typography variant="h6">{data.summary.totalProducts}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Total Revenue</Typography>
//               <Typography variant="h6">{currencySymbol} {data.summary.totalRevenue.toFixed(2)}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Best Seller</Typography>
//               <Typography variant="body2">{data.summary.bestSellingProduct}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Avg Turnover</Typography>
//               <Typography variant="h6">{data.summary.averageStockTurnover}</Typography>
//             </Paper>
//           </Grid>
//         </Grid>

//         <Typography variant="h6" gutterBottom>
//           Products
//         </Typography>
//         <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
//           <Table size="small" stickyHeader>
//             <TableHead>
//               <TableRow>
//                 <TableCell>Product</TableCell>
//                 <TableCell>Category</TableCell>
//                 <TableCell align="right">Sold</TableCell>
//                 <TableCell align="right">Revenue</TableCell>
//                 <TableCell align="right">Avg Price</TableCell>
//                 <TableCell align="right">Turnover</TableCell>
//               </TableRow>
//             </TableHead>
//             <TableBody>
//               {data.products.slice(0, 50).map((product: any) => (
//                 <TableRow key={product.productId}>
//                   <TableCell>
//                     {product.productName}
//                     {product.isFeatured && (
//                       <Chip label="Featured" size="small" color="primary" sx={{ ml: 1 }} />
//                     )}
//                   </TableCell>
//                   <TableCell>{product.category}</TableCell>
//                   <TableCell align="right">{product.totalSold}</TableCell>
//                   <TableCell align="right">{currencySymbol} {product.revenue.toFixed(2)}</TableCell>
//                   <TableCell align="right">{currencySymbol} {product.averagePrice.toFixed(2)}</TableCell>
//                   <TableCell align="right">{product.stockTurnover}</TableCell>
//                 </TableRow>
//               ))}
//             </TableBody>
//           </Table>
//         </TableContainer>
//       </Box>
//     );
//   };

//   const renderRevenueReport = () => {
//     const data = report.data;
//     if (!data.dailyRevenue) return null;

//     return (
//       <Box>
//         <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
//           Summary
//         </Typography>
//         <Grid container spacing={2} sx={{ mb: 3 }}>
//           <Grid item xs={6} sm={4} md={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Total Revenue</Typography>
//               <Typography variant="h6">{currencySymbol} {data.summary.totalRevenue.toFixed(2)}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={4} md={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Net Revenue</Typography>
//               <Typography variant="h6">{currencySymbol} {data.summary.totalNetRevenue.toFixed(2)}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={4} md={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Total Orders</Typography>
//               <Typography variant="h6">{data.summary.totalOrders}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={4} md={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Avg Order Value</Typography>
//               <Typography variant="h6">{currencySymbol} {data.summary.averageOrderValue.toFixed(2)}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={4} md={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Avg Daily Revenue</Typography>
//               <Typography variant="h6">{currencySymbol} {data.summary.averageDailyRevenue.toFixed(2)}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={4} md={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Growth Rate</Typography>
//               <Stack direction="row" alignItems="center" spacing={0.5}>
//                 <Typography variant="h6" color={data.summary.growthRate >= 0 ? 'success.main' : 'error.main'}>
//                   {data.summary.growthRate >= 0 ? '+' : ''}{data.summary.growthRate.toFixed(2)}%
//                 </Typography>
//                 {data.summary.growthRate >= 0 ? (
//                   <TrendingUpIcon color="success" fontSize="small" />
//                 ) : (
//                   <TrendingDownIcon color="error" fontSize="small" />
//                 )}
//               </Stack>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={4} md={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Highest Revenue Day</Typography>
//               <Typography variant="body2">{data.summary.highestRevenueDay}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={4} md={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Total Discount</Typography>
//               <Typography variant="h6">{currencySymbol} {data.summary.totalDiscount.toFixed(2)}</Typography>
//             </Paper>
//           </Grid>
//         </Grid>

//         {/* Top Products */}
//         {data.topProducts && data.topProducts.length > 0 && (
//           <Box sx={{ mb: 3 }}>
//             <Typography variant="subtitle1" gutterBottom>
//               Top Products by Revenue
//             </Typography>
//             <Grid container spacing={2}>
//               {data.topProducts.slice(0, 5).map((product: any, index: number) => (
//                 <Grid item xs={12} sm={6} md={4} key={index}>
//                   <Paper sx={{ p: 2 }}>
//                     <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
//                       <Typography variant="body2" fontWeight="medium">
//                         {product.productName}
//                       </Typography>
//                       <Chip 
//                         label={`${product.percentageOfTotal.toFixed(1)}%`}
//                         size="small"
//                         color="primary"
//                       />
//                     </Stack>
//                     <Typography variant="caption" color="text.secondary" display="block">
//                       Revenue: {currencySymbol} {product.revenue.toFixed(2)}
//                     </Typography>
//                     <Typography variant="caption" color="text.secondary" display="block">
//                       Units Sold: {product.totalSold}
//                     </Typography>
//                     <LinearProgress 
//                       variant="determinate" 
//                       value={product.percentageOfTotal} 
//                       sx={{ mt: 1, height: 6, borderRadius: 1 }}
//                     />
//                   </Paper>
//                 </Grid>
//               ))}
//             </Grid>
//           </Box>
//         )}

//         {/* Top Categories */}
//         {data.topCategories && data.topCategories.length > 0 && (
//           <Box sx={{ mb: 3 }}>
//             <Typography variant="subtitle1" gutterBottom>
//               Top Categories by Revenue
//             </Typography>
//             <Grid container spacing={2}>
//               {data.topCategories.slice(0, 5).map((category: any, index: number) => (
//                 <Grid item xs={12} sm={6} md={4} key={index}>
//                   <Paper sx={{ p: 2 }}>
//                     <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
//                       <Typography variant="body2" fontWeight="medium">
//                         {category.categoryName}
//                       </Typography>
//                       <Chip 
//                         label={`${category.percentageOfTotal.toFixed(1)}%`}
//                         size="small"
//                         color="secondary"
//                       />
//                     </Stack>
//                     <Typography variant="caption" color="text.secondary" display="block">
//                       Revenue: {currencySymbol} {category.revenue.toFixed(2)}
//                     </Typography>
//                     <LinearProgress 
//                       variant="determinate" 
//                       value={category.percentageOfTotal} 
//                       color="secondary"
//                       sx={{ mt: 1, height: 6, borderRadius: 1 }}
//                     />
//                   </Paper>
//                 </Grid>
//               ))}
//             </Grid>
//           </Box>
//         )}

//         {/* Daily Revenue Table */}
//         <Typography variant="h6" gutterBottom>
//           Daily Revenue
//         </Typography>
//         <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
//           <Table size="small" stickyHeader>
//             <TableHead>
//               <TableRow>
//                 <TableCell>Date</TableCell>
//                 <TableCell align="right">Orders</TableCell>
//                 <TableCell align="right">Revenue</TableCell>
//                 <TableCell align="right">Net Revenue</TableCell>
//                 <TableCell align="right">Avg Order</TableCell>
//                 <TableCell align="right">Tax</TableCell>
//                 <TableCell align="right">Shipping</TableCell>
//               </TableRow>
//             </TableHead>
//             <TableBody>
//               {data.dailyRevenue.map((day: any, index: number) => (
//                 <TableRow key={index}>
//                   <TableCell>
//                     {day.date instanceof Date 
//                       ? day.date.toLocaleDateString() 
//                       : 'N/A'}
//                   </TableCell>
//                   <TableCell align="right">{day.totalOrders}</TableCell>
//                   <TableCell align="right">
//                     <Typography variant="body2" fontWeight="medium">
//                       {currencySymbol} {day.totalRevenue.toFixed(2)}
//                     </Typography>
//                   </TableCell>
//                   <TableCell align="right">{currencySymbol} {day.netRevenue.toFixed(2)}</TableCell>
//                   <TableCell align="right">{currencySymbol} {day.averageOrderValue.toFixed(2)}</TableCell>
//                   <TableCell align="right">{currencySymbol} {day.totalTax.toFixed(2)}</TableCell>
//                   <TableCell align="right">{currencySymbol}{day.totalShipping.toFixed(2)}</TableCell>
//                 </TableRow>
//               ))}
//             </TableBody>
//           </Table>
//         </TableContainer>
//       </Box>
//     );
//   };

//   const renderReportData = () => {
//     switch (report.reportType) {
//       case 'sales':
//         return renderSalesReport();
//       case 'inventory':
//         return renderInventoryReport();
//       case 'purchase_orders':
//         return renderPurchaseOrderReport();
//       case 'low_stock':
//         return renderLowStockReport();
//       case 'warehouse_performance':
//         return renderWarehousePerformanceReport();
//       case 'product_performance':
//         return renderProductPerformanceReport();
//       case 'revenue':
//         return renderRevenueReport();
//       default:
//         return <Typography>Report type not supported</Typography>;
//     }
//   };

//   return (
//     <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
//       <DialogTitle>
//         <Stack direction="row" justifyContent="space-between" alignItems="center">
//           <Typography variant="h6">{report.reportName}</Typography>
//           <IconButton onClick={onClose} size="small">
//             <CloseIcon />
//           </IconButton>
//         </Stack>
//       </DialogTitle>
//       <DialogContent dividers>
//         <Box sx={{ mb: 3 }}>
//           <Grid container spacing={2}>
//             <Grid item xs={12} sm={6}>
//               <Typography variant="body2" color="text.secondary">Report Type</Typography>
//               <Chip label={report.reportType.replace('_', ' ')} color="primary" size="small" sx={{ mt: 0.5 }} />
//             </Grid>
//             <Grid item xs={12} sm={6}>
//               <Typography variant="body2" color="text.secondary">Generated At</Typography>
//               <Typography variant="body1">
//                 {report.generatedAt?.toLocaleDateString()} {report.generatedAt?.toLocaleTimeString()}
//               </Typography>
//             </Grid>
//             <Grid item xs={12} sm={6}>
//               <Typography variant="body2" color="text.secondary">Generated By</Typography>
//               <Typography variant="body1">{report.generatedByName}</Typography>
//             </Grid>
//             <Grid item xs={12} sm={6}>
//               <Typography variant="body2" color="text.secondary">Total Records</Typography>
//               <Typography variant="body1">{report.summary?.totalRecords || 0}</Typography>
//             </Grid>
//             {report.description && (
//               <Grid item xs={12}>
//                 <Typography variant="body2" color="text.secondary">Description</Typography>
//                 <Typography variant="body1">{report.description}</Typography>
//               </Grid>
//             )}
//           </Grid>
//         </Box>

//         <Divider sx={{ mb: 2 }} />

//         {renderReportData()}
//       </DialogContent>
//       <DialogActions>
//         <Button onClick={onClose}>Close</Button>
//         <Button
//           onClick={handleDownloadPDF}
//           variant="contained"
//           startIcon={<PdfIcon />}
//         >
//           Download PDF
//         </Button>
//       </DialogActions>
//     </Dialog>
//   );
// };

// export default ReportDetailsDialog;